import './Shell.css'

import { Suspense, useEffect, useState } from 'react'
import type { UIMatch } from 'react-router'
import { Outlet, useLocation, useMatches } from 'react-router'

import type { RouteHandle } from '~/router/types'

import { Footer } from './regions/Footer/Footer'
import { Nav, type NavState, setLastRoute } from './regions/Nav/Nav'
import { TitleBar } from './regions/TitleBar/TitleBar'

const getShellNavState = (showNav: boolean, navState: NavState) => {
  if (!showNav) {
    return 'hidden'
  }
  return navState === 'expanded' ? 'expanded' : 'collapsed'
}

const DocumentTitle = ({ title }: { title: string }) => {
  // Keep the browser tab title in sync with the active route title.
  useEffect(() => {
    document.title = title
  }, [title])
  return null
}

export const Shell = () => {
  const [navState, setNavState] = useState<NavState>('expanded')
  const { pathname } = useLocation()

  const matches = useMatches() as UIMatch<unknown, RouteHandle | undefined>[]

  const handle = matches.reduce<Partial<RouteHandle>>((mergedHandle, routeMatch) => {
    const routeHandle = routeMatch.handle
    if (!routeHandle) {
      return mergedHandle
    }
    const defined = Object.fromEntries(
      Object.entries(routeHandle).filter(
        ([, handleValue]) => handleValue !== null && handleValue !== undefined,
      ),
    )
    return { ...mergedHandle, ...defined }
  }, {})

  const showNav = handle.showNav ?? true
  const showTitleBar = handle.showTitleBar ?? true
  const showFooter = handle.showFooter ?? true
  const shellNavState = getShellNavState(showNav, navState)
  const title = matches.at(-1)?.handle?.title ?? 'Doss'
  const breadcrumb = matches.at(-1)?.handle?.breadcrumb

  // Update last-known route per section on every navigation
  useEffect(() => {
    const section = pathname.split('/')[1]
    if (section) {
      setLastRoute(section, pathname)
    }
  }, [pathname])

  const handleToggleNav = () => {
    setNavState((prev) => (prev === 'expanded' ? 'collapsed' : 'expanded'))
  }
  const handlePeekStart = () => {
    if (navState !== 'expanded') {
      setNavState('peek')
    }
  }
  const handlePeekEnd = () => {
    if (navState === 'peek') {
      setNavState('collapsed')
    }
  }

  return (
    <>
      <DocumentTitle title={title} />
      <div
        className="shell"
        data-nav-state={shellNavState}
        data-show-footer={String(showFooter)}
        data-show-title={String(showTitleBar)}
      >
        {showTitleBar && (
          <TitleBar
            breadcrumb={breadcrumb}
            isNavCollapsed={navState !== 'expanded'}
            onPeekEnd={handlePeekEnd}
            onPeekStart={handlePeekStart}
            onToggleNav={handleToggleNav}
            title={title}
          />
        )}
        <div className="canvas">
          <Suspense fallback={null}>
            {/* TODO: Add a loading component */}
            <Outlet />
          </Suspense>
        </div>
        {showFooter && <Footer />}
      </div>
      {showNav && (
        <div
          className="shell-nav-overlay"
          data-state={navState}
          onMouseEnter={handlePeekStart}
          onMouseLeave={handlePeekEnd}
        >
          <Nav navState={navState} />
        </div>
      )}
    </>
  )
}
